import React, { Component } from 'react';
import { Button } from 'react-materialize';
import IframeComm from 'react-iframe-comm';
import { FormattedMessage, injectIntl } from 'react-intl';

import settings from '../../libs/config';
import Logger from '../../functions/logging';

import PCORE_EVENT from '../../constants/pilotly-player';

import * as auth from '../../functions/auth';
import * as actions from '../../redux/actions';
import * as helpers from '../../functions/helpers';
import * as TargetProcess from '../../functions/targetProcess';
import {
	isVideoStudy,
	getCompletedTimecode
} from '../../functions/study';

import Feedback from './Feedback';
import PageLoader from './PageLoader';
import PilotlyModal from './PilotlyModal';
import InvalidUrl from './InvalidUrl';
import FadeIn from './FadeIn';
import { penalizeUser } from '../../api/penalize';
import {
	updateUserContentProfile,
	updateUserSubscription,
	terminateUser,
	initUserQuestionAssignment,
	updateUserQuestionAssignment,
	getAllUserQuestionAssignments
} from '../../api/user';
import { setPlayerSessionUserId } from '../../api/player';
import { isNumber } from '../../functions/utils';

import { createPanelResponse, createSceneResponse, getUserResponsesInfo } from '../../api/responses';
import { createErrorNotification } from '../../api/notify';
import { requestFollowUp } from '../../api/followUp';

import localeMessages from '../../locales/player';
import { OpenEnded, Survey } from '@groupflix/survey/build/index';
import SURVEY_METHODS from '@groupflix/survey/libs/utils';
import QUESTION_HELPERS from '@groupflix/survey/libs/questions';

// import GetStarted from '../landing/GetStarted';
/* Google analytics */
import MediaPlayerStatus from '../../analytics/analytics-events/MediaPlayerStatus';
import { MediaPlayerStatusEvent } from '../../analytics/analytics-events/constants';
import { connect } from 'react-redux';
import { BAD_RESPONDANT_CODE, EJECT_CODE } from '../../constants/screenCodes';
import { enableFullscreen, exitFullscreen, getIsFullscreenEnabled } from '../../functions/fullscreen';
import { LANDSCAPE_ORIENTATION, PORTRAIT_ORIENTATION, isOrientationLockAvailable } from '../../functions/orientation';

import * as SURVEY_VALIDATIONS from '@groupflix/survey/libs/validations';
import * as PANEL_HELPERS from '../../functions/panels';
import { STALLED } from '../../constants/screenReasons';
import { THANK_YOU } from '../../constants/feedbackCodes';
import '../../static/PilotlyPlayer.css';
import DeviceHelper from '../../functions/device';
import { getEmojiIcon } from '../../functions/emotion';

const SESSION_LENGTH_CODE = "st";
const BAD_RESPONDANT_DISABLE_PARAM = "0";
const LINKED_MODERATED_STUDY = 'linked_moderated_study';
const PANEL_RESPONSE_SUBMIT_LIMIT = 3;

class PilotlyPlayer extends Component {
	constructor(props) {
		super(props);
		Logger.log(PilotlyPlayer.name, "constructor()", props);

		const allParamsValid = this.validateParams();
		const fetchedPanel = props.fetchedPanel;

		this.answeredQuestions = {};
		this.goodwill = SURVEY_VALIDATIONS.returnInitGoodwill(fetchedPanel);
		this.isOrientationLocked = false;
		this.isPlayerReady = false;
		this.isShowingTuneoutModal = false;
		this.isShowingTuneoutQuestion = false;
		this.landscapeOrientationMediaQuery = null;
		this.questionSetMapHintTime = QUESTION_HELPERS.returnQuestionSetMapHintTime(fetchedPanel);
		this.maximumQuestionSetIndex = QUESTION_HELPERS.returnMaximumQuestionSetIndex(fetchedPanel);
		this.timecode = null;
		this.userActiveTimer = null;
		this.wakeLockSentinel = null;

		this.submittingResponse = false;
		this.handlingComplete = false;

		this.emotionLikeAnswers = null;

		this.state = {
			postMessageData: { test: 'ready' },
			feedbackType: false,
			showLoader: true,
			videoError: allParamsValid ? null : 'invalidUrl',
			error: {},
			answerTracker: [],
			pathTracker: [],
			questionSetIndex: 0,
			questionIndex: 1, // the first question of any survey is never displayed, so the index is incremented by 1
			seekedTimecode: 0,
			isResumingStudy: false,
			isSurveyActive: false,
			receivedDisplaySurveyEvent: false,
			requiredOrientation: null,
			tuneoutQuestion: null,

			assignedQuestionVersions: []
		};

		if (allParamsValid) {
			const params = this.getParams();
			const { fetchedGroup, targetParams, currentUser } = props;

			const locale = this.getLanguage();

			const isTester = TargetProcess.isTester(targetParams);
			const resume = TargetProcess.isResumeEnabled(targetParams);
			const isSurveyOnly = helpers.isGroupSurveyOnly(fetchedGroup);
			const skipIntro = TargetProcess.isSkippingIntro(targetParams);
			const allowFullScreen = DeviceHelper.isMobile ? false : true;
			const panelSegment = fetchedGroup && fetchedGroup.panel_segment && fetchedGroup.panel_segment[params.panelSegmentIndex];
			const sentimentDisabled = panelSegment && panelSegment.portal_params && panelSegment.portal_params.sentiment === false;
			const tuneoutsDisabled = panelSegment && panelSegment.portal_params && panelSegment.portal_params.tuneouts === false;
			const sentimentFullOpacity = panelSegment && panelSegment.sentiment_full_opacity === true;
			const url = new URL(settings.portal_player_url);

			url.searchParams.set("gid", params.group_id);
			url.searchParams.set("panel_segment", params.panelSegmentIndex);
			url.searchParams.set("user_id", currentUser.user_id);
			url.searchParams.set("language", locale);
			url.searchParams.set("interactive", 1);
			if (targetParams && targetParams.partner) url.searchParams.set("redir", 1);
			if (isTester) url.searchParams.set("seekEnabled", 1);
			if (targetParams && targetParams.br === BAD_RESPONDANT_DISABLE_PARAM) url.searchParams.set("br", 0);
			if (!resume) url.searchParams.set("timecode", 0);
			if (isSurveyOnly) url.searchParams.set("so", 1);
			if (skipIntro) url.searchParams.set("nointro", 1);
			if (targetParams && targetParams.vo === 1) url.searchParams.set("vo", 1);

			//Why did we created the mobileweb parameter?
			//Removing this parameter from Portal Player, makes it send the DISPLAY_SURVEY event to all the mobile environments that are using Portal Player.
			//Mobile Portal Web, mobile app and TV app.
			//if (DeviceHelper.isMobile) url.searchParams.set("mobileweb", 1);

			if (sentimentDisabled) url.searchParams.set("sentiment", 0);
			if (tuneoutsDisabled) url.searchParams.set("tuneouts", 0);
			if (sentimentFullOpacity) url.searchParams.set("sentimentFullOpacity", 1);

			const playerUrl = url.toString();

			Logger.log(PilotlyPlayer.name, "constructor() playerUrl", playerUrl);

			this.attributes = {
				allowFullScreen,
				src: playerUrl,
				serializeMessage: false,
				width: "100%",
				height: "100%",
				frameBorder: 0,
				scrolling: "no",
				targetOrigin: 'pilot.ly',
				allow: 'encrypted-media; microphone; camera'
			};
		}

		this.confirmTuneOut = this.confirmTuneOut.bind(this);
		this.fullscreenChanged = this.fullscreenChanged.bind(this);
		this.orientationChanged = this.orientationChanged.bind(this);
		this.handleBadRespondents = this.handleBadRespondents.bind(this);
		this.handlePanelResponse = this.handlePanelResponse.bind(this);
		this.handleProgressUpdate = this.handleProgressUpdate.bind(this);
		this.handleQuestionIndexOverride = this.handleQuestionIndexOverride.bind(this);
		this.handleSurveyModuleTermination = this.handleSurveyModuleTermination.bind(this);
		this.handleTuneoutAnswer = this.handleTuneoutAnswer.bind(this);
		this.handleTuneOutNo = this.handleTuneOutNo.bind(this);
		this.handleUserActive = this.handleUserActive.bind(this);
		this.onChangeVisibility = this.onChangeVisibility.bind(this);
		this.onCloseModal = this.onCloseModal.bind(this);
		this.disableKeepAwake = this.disableKeepAwake.bind(this);
		this.handleTrackerUpdate = this.handleTrackerUpdate.bind(this);
		this.keepScreenAwake = this.keepScreenAwake.bind(this);
		this.onWakeLockRelease = this.onWakeLockRelease.bind(this);
		this.requestWakeLock = this.requestWakeLock.bind(this);
		this.requestWakeLockAgain = this.requestWakeLockAgain.bind(this);

		this.handleMaxDiffVersionRequest = this.handleMaxDiffVersionRequest.bind(this);
		this.handleQuestionVersionComplete = this.handleQuestionVersionComplete.bind(this);

		// Parse emotion question map to to display the emotion tray in the ActivityPrompt in survey module.
		// In the ActivityPrompt we display the emotion tray with the like answers.
		// This keeps parity with desktop.
		// https://github.com/groupflix/portal-player/blob/master/players/js/interactive.layer.js#L891
		if (DeviceHelper.isMobile) {
			this.processEmotionQuestionMap();
		}
	}

	async componentDidMount() {
		const sendthis = { message: { eventCall: 'VIDEOPLAYER_MOUNTED', params: { timecode: 0 } } };
		this.handleAnalyticsEvent(sendthis);

		if (DeviceHelper.isMobile) {
			document.body.classList.add('blackBackground');
		}

		this.listenToVisibilityChange();

		await this.keepScreenAwake();

		Logger.log(this.constructor.name, "componentDidMount()", 'this.props.currentUser', this.props.currentUser);
		try {
			setPlayerSessionUserId(this.props.currentUser);
		}
		catch (error) {
			Logger.log(this.constructor.name, "componentDidMount() setPlayerSessionUserId error", error);
		}

		await this.loadAnsweredQuestions();
		await this.loadUserQuestionAssignments();
	}

	componentWillUnmount() {
		this.disableKeepAwake();
		this.stopUserActiveTimer();
		this.removeVisibilityChangeListener();
	}

	validateParams = () => {
		const params = this.getParams();
		if (params
			&& params.group_id
			&& isNumber(params.panelSegmentIndex)
			&& this.props.currentUser
			&& this.props.currentUser.user_id
		) {
			return true;
		}

		return false;
	}

	getParams() {
		const { fetchedGroup, fetchedGroupPanelSegment, targetParams } = this.props;

		let group_id = null;
		let panelSegmentIndex = null;
		let panel_id = null;
		let survey_id = null;

		if (targetParams
			&& targetParams.gid
			&& (isNumber(targetParams.panelSegmentIndex) || isNumber(targetParams.ps))
		) {
			group_id = targetParams.gid;
			panelSegmentIndex = targetParams.panelSegmentIndex || targetParams.ps;
		}
		else if (fetchedGroup
			&& fetchedGroup._id
			&& isNumber(fetchedGroupPanelSegment)
		) {
			group_id = fetchedGroup._id;
			panelSegmentIndex = fetchedGroupPanelSegment;
		}

		if (fetchedGroup
			&& fetchedGroup.panel_segment
			&& fetchedGroup.panel_segment[panelSegmentIndex]
			&& (fetchedGroup.panel_segment[panelSegmentIndex].panel || fetchedGroup.panel_segment[panelSegmentIndex].survey_id)
		) {
			panel_id = fetchedGroup.panel_segment[panelSegmentIndex].panel;
			survey_id = fetchedGroup.panel_segment[panelSegmentIndex].survey_id;
		}

		return {
			group_id,
			panelSegmentIndex: parseInt(panelSegmentIndex),
			panel_id,
			survey_id
		};
	}

	getDeviceType = () => {
		switch (DeviceHelper.osName) {
			case 'macOS':
				return 'iMac';
			case 'Windows':
				return 'windowsPC';
		}
		return DeviceHelper.osName;
	}

	async loadUserQuestionAssignments() {
		const currentUser = this.props.currentUser;
		const userId = currentUser.user_id;
		const userToken = auth.getToken();
		const { group_id, panel_id, panelSegmentIndex, survey_id } = this.getParams();
		try {
			const results = await getAllUserQuestionAssignments(userId, userToken, { group_id, panel_segment_index: panelSegmentIndex, survey_id });
			if (results && results.data && results.data.length) {
				const assignedQuestionVersions = results.data.map((result) => {
					return {
						qid: result.qid,
						question_type: result.question_type,
						version_id: result.version_id,
					};
				});
				this.setState({ assignedQuestionVersions });
			}
		} 
		catch (error) {
			Logger.log(this.constructor.name, "loadUserQuestionAssignments() error", error);
		}
	}

	// for survey module use
	async handlePanelResponse(
		selectedAnswers,
		selectedPaths,
		question,
		screenCode,
		questionValidation,
		positions,
		language,
		submitCount
	) {
		this.submittingResponse = true;
		const questionType = question.type;
		submitCount = !submitCount ? 0 : submitCount;
		const { currentUser, fetchedGroup, fetchedPanel } = this.props;
		const params = this.returnParams();
		const panelSegmentIndex = params.panelSegmentIndex;
		const panelResponse = SURVEY_METHODS.buildPanelResponse(selectedAnswers, question, questionValidation, positions, language, fetchedGroup, fetchedPanel, panelSegmentIndex, currentUser);
		Logger.log(this.constructor.name, "handlePanelResponse()", 'Submitting panel response', panelResponse);


		if (PANEL_HELPERS.validateQuestionSubmission(question, panelResponse)) {
			try {
				const userToken = auth.getToken();
				const result = await createPanelResponse(panelResponse, userToken);
				if (result) {
					Logger.log(this.constructor.name, "handlePanelResponse() result", result);
					this.submittingResponse = false;
					if (this.handlingComplete) this.handleComplete();
				}
				else {
					Logger.log(this.constructor.name, "Failed to post panel response. Submitting again - count", submitCount + 1);

					if (submitCount < PANEL_RESPONSE_SUBMIT_LIMIT) {
						submitCount++;
						this.handlePanelResponse(selectedAnswers, selectedPaths, question, screenCode, questionValidation, positions, language, submitCount);
					}
					else if (submitCount >= PANEL_RESPONSE_SUBMIT_LIMIT) {
						this.submittingResponse = false;
						this.handleSurveyModuleTermination(fetchedGroup, BAD_RESPONDANT_CODE, 'dataquality', question, STALLED);
					}
				}
			}
			catch (error) {
				Logger.log(this.constructor.name, "Panel Response Failed", error);

				if (submitCount < PANEL_RESPONSE_SUBMIT_LIMIT) {
					submitCount++;
					this.handlePanelResponse(selectedAnswers, selectedPaths, question, screenCode, questionValidation, positions, language, submitCount);
				}
				else if (submitCount >= PANEL_RESPONSE_SUBMIT_LIMIT) {
					this.submittingResponse = false;
					this.handleSurveyModuleTermination(fetchedGroup, BAD_RESPONDANT_CODE, 'dataquality', question, STALLED);
				}
			}
		}
		else {
			Logger.log(this.constructor.name, "handlePanelResponse() - skipping, no avaliable answer", questionType, "Continuing to next question");
			this.submittingResponse = false;
		}
	}

	// for player use
	async postPanelResponse(responseData) {
		const { fetchedPanel } = this.props;

		let user_token = auth.getToken();
		const params = this.getParams();

		let panelResponse = {
			group_id: params.group_id,
			panel_segment: params.panelSegmentIndex,
			panel_id: helpers.mongoIdToString(responseData.panelId),
			qid: responseData.question,
			aid: responseData.answer,
			qtype: responseData.qtype,
			position: responseData.position,
			duration: responseData.duration,
			feedback_pro: this.props.currentUser.user_profile_privacy.feedback_pro,
			user_id: this.props.currentUser.user_id,
			started_at: responseData.started_at,
			timecode: responseData.timecode
		};
		if (responseData.language) {
			panelResponse.language = responseData.language;
		}
		//:NOTE:: conversational open ends submit once per chain: aid stays the verbatim
		//first answer and each AI follow-up rides along as a numbered {n, question, answer}
		//pair. Guarded, so a response with no follow-ups is byte-identical to before.
		if (Array.isArray(responseData.followups) && responseData.followups.length) {
			panelResponse.followups = responseData.followups;
		}
		if (responseData.qtype === "maxdiff") {
			panelResponse.tasks_order = responseData.tasks_order;
			panelResponse.tasks_responses = responseData.tasks_responses;
			panelResponse.version_id = responseData.version_id;
			panelResponse.version_title = responseData.version_title;
			panelResponse.best_worst_results = responseData.best_worst_results;
		}

		Logger.log(this.constructor.name, "postPanelResponse()", 'Submitting panel response', panelResponse);

		const errorMethodCallMessage = { event: PCORE_EVENT.METHOD_CALL, method: 'confirmAnswerResponse', value: { status: "fail", request: responseData } };

		try {
			const result = await createPanelResponse(panelResponse, user_token);

			if (result) {
				Logger.log(this.constructor.name, "postPanelResponse() result", result);
				this.sendMessage({ event: PCORE_EVENT.METHOD_CALL, method: 'confirmAnswerResponse', value: { status: "success", request: responseData } });
			}
			else {
				Logger.log(this.constructor.name, "Failed to post panel response");
				this.sendMessage(errorMethodCallMessage);
			}
		} catch (error) {
			this.sendMessage(errorMethodCallMessage);
			Logger.log(this.constructor.name, "Panel Response Failed", error);
		}
	}

	async postSceneResponse(sentiment) {
		let user_token = auth.getToken();
		const params = this.getParams();
		let ext_video_id = this.props.fetchedEpisode && this.props.fetchedEpisode.ext_video_id ? this.props.fetchedEpisode.ext_video_id : "";
		//:NOTE:: cant submit data with no userid
		//let user_id = this.props.currentUser && this.props.currentUser.user_id ? this.props.currentUser.user_id : "";


		try {
			const sceneResponse = {
				group_id: params.group_id,
				panel_segment: params.panelSegmentIndex,
				ext_video_id: ext_video_id,
				response: sentiment.response,
				emotions : (sentiment.emotions) ? sentiment.emotions : [],
				scenecode: sentiment.scene,
				timecode: sentiment.timecode,
				user_id: this.props.currentUser.user_id
			};

			Logger.log(this.constructor.name, "postSceneResponse()", 'Submitting scene response', sceneResponse);

			const result = await createSceneResponse(sceneResponse, user_token);

			if (result) {
				Logger.log(this.constructor.name, "postSceneResponse()", 'RESULT', result);
			}
			else {
				Logger.log(this.constructor.name, "Failed to post scene response");
			}
		}
		catch (error) {
			Logger.log(this.constructor.name, "Scene Response Failed", error);
		}
	}

	async notifyError(message) {
		let user_token = auth.getToken();
		const params = this.getParams();

		try {
			const data = {
				group_id: params.group_id,
				panel_segment: params.panelSegmentIndex,
				user_id: this.props.currentUser.user_id,
				error: message.error,
				source: message.source,
				code: message.code,
				type: message.type,
				message: message.message,
				timecode: message.timecode,
				player_state: message.player_state,
				player_resumed: message.player_resumed,
				video_duration: message.video_duration,
				panel_ready: message.panel_ready,
				survey_status: message.survey_status, // active or inactive
				survey_type: message.survey_type, // preroll, invideo, postroll
				survey_qid: message.survey_qid, // last question seen
				survey_aid: message.survey_aid, // last answer submitted	
				browser: message.browser,
				os: message.os,
				platform: message.platform,
				uagent: message.uagent,
				playerInfo: message.playerInfo,
				osName: DeviceHelper.osName,
				browserName: DeviceHelper.browserName
			};

			Logger.log(this.constructor.name, "notifyError()", 'Submitting error', data);

			const result = await createErrorNotification(data, user_token);

			if (result) {
				Logger.log(this.constructor.name, "notifyError()", 'RESULT', result);
			}
			else {
				Logger.log(this.constructor.name, "Failed to send error information");
			}

		} catch (error) {
			Logger.log(this.constructor.name, "Failed to send error information", error);
		}
	}

	async loadAnsweredQuestions() {

		const currentUser = this.props.currentUser;
		const userId = currentUser.user_id;
		const userToken = auth.getToken();

		const { group_id, panel_id, panelSegmentIndex, survey_id } = this.getParams();

		try {
			Logger.log(this.constructor.name, "loadAnsweredQuestions() params:", group_id, panel_id, panelSegmentIndex, survey_id, userId);

			const result = await getUserResponsesInfo(group_id, panel_id, panelSegmentIndex, survey_id, userId, userToken);
			const answeredQuestions = result.answeredQuestions;

			if (answeredQuestions && answeredQuestions.length) {

				let answeredQuestionsMap = {};

				answeredQuestions.forEach((answeredQuestion) => {
					const answeredQuestionId = answeredQuestion.qid;
					answeredQuestionsMap[answeredQuestionId] = answeredQuestion;
				});

				this.answeredQuestions = answeredQuestionsMap;

				this.setState({ pathTracker: this.returnRestoredPathTracker() });
			}
		}
		catch (error) {
			Logger.log(this.constructor.name, "error:", error);
		}
	}

	// resumeStudy() only replays the questions for the hint_time being displayed
	// (questionSetMapHintTime[hintTime]), so paths recorded in an earlier question set
	// — e.g. a hidden conditionalAidSubmit in the screener — are lost whenever this
	// component remounts before that later set renders.
	//
	// Only paths are restored. answerTracker is left alone deliberately: it feeds
	// returnHiddenLogic(), which matches entries against condition aids without
	// checking qid, so seeding it would change which hidden questions fire.
	returnRestoredPathTracker() {
		const questions = this.props.fetchedPanel && this.props.fetchedPanel.questions;

		let pathTracker = [];

		if (!questions || !questions.length) return pathTracker;

		questions.forEach((question) => {

			const answeredQuestion = this.answeredQuestions[question.qid];
			if (!answeredQuestion || !answeredQuestion.paths) return;

			answeredQuestion.paths.forEach((path) => {
				if (path && !pathTracker.includes(path)) pathTracker.push(path);
			});
		});

		return pathTracker;
	}

	resumeStudy(questions) {

		const { fetchedGroup, fetchedPanel, intl, targetParams } = this.props;

		let isResumingStudy = false;
		let newState = {};
		let willShowQuestion = false;

		let { answerTracker, pathTracker } = this.state;

		const numberOfAnsweredQuestions = Object.keys(this.answeredQuestions).length;

		if (numberOfAnsweredQuestions > 0) {
			
			//Get the latest question from the question set that was answered by the user.
			//If a question is found, give it and its answers to the survey module to get the next question that the user needs to answer.

			let latestAnsweredQuestionIndex = -1;
			const numberOfQuestions = questions.length;

			for (let i = numberOfQuestions - 1; i >= 0; i--) {

				const question = questions[i];
				const questionId = question.qid;

				if (this.answeredQuestions.hasOwnProperty(questionId)) {
					latestAnsweredQuestionIndex = i;
					break;
				}
			}

			if (latestAnsweredQuestionIndex >= 0) {

				let nextQuestionIndex;
				let nextQuestionSetIndex;
				let updatedGoodWill = this.goodwill;

				const language = this.getLanguage();

				const isTester = TargetProcess.isTester(targetParams);

				const interrupt = questions.some((question, index) => {
					if (index <= latestAnsweredQuestionIndex) {

						const questionId = question.qid;

						if (this.answeredQuestions.hasOwnProperty(questionId)) {

							const answeredQuestion = this.answeredQuestions[questionId];
							const questionIndex = question.index;
							const questionSetIndex = question.qset_index;
							const questionStartTime = (new Date()).getTime();

							let selectedPaths = [];
							const answeredQuestionPaths = answeredQuestion.paths;
							if (answeredQuestionPaths && answeredQuestionPaths.length) {
								selectedPaths = answeredQuestionPaths;
							}

							let selectedAnswers;

							const questionType = question.type;
							const response = answeredQuestion.response;

							if (questionType === 'openEnded') {
								const answer = response[0];
								selectedAnswers = answer;
							}
							else if (questionType !== 'activityPrompt' && questionType !== 'messageCard') {
								if (response) {
									selectedAnswers = response;
								}
								else {
									selectedAnswers = [];
								}
							}

							const answerPositions = SURVEY_METHODS.returnAnswerPositions(selectedAnswers, question);
							const progressInfo = SURVEY_METHODS.handlePanelProgress(
								question,
								questionIndex,
								questionSetIndex,
								pathTracker,
								answerTracker,
								fetchedPanel,
								language,
								null,
								questionStartTime,
								selectedAnswers,
								selectedPaths,
								{},
								answerPositions,
								null
							);

							nextQuestionSetIndex = progressInfo.questionSetIndex;

							const screenCode = progressInfo.screenCode;

							//If the survey module returns screen code thankyou or the the next question set is greater than the last question set,
							//this means that the user finished answering the questions for the study.
							//For survey-only studies there is no video to hand control back to, so the study is also
							//finished when none of the remaining question sets (from nextQuestionSetIndex onward) have
							//displayable questions — e.g. only the exit survey prompt is left, which the survey module never renders.
							const isSurveyOnly = helpers.isGroupSurveyOnly(fetchedGroup);
							const hasRemainingDisplayableQuestions = this.hasDisplayableQuestionsFromSet(fetchedPanel, nextQuestionSetIndex);

							if (screenCode === THANK_YOU
								|| nextQuestionSetIndex > this.maximumQuestionSetIndex
								|| (isSurveyOnly && !hasRemainingDisplayableQuestions)
							) {
								this.handleQuestionVersionComplete();
								this.handleComplete();
								return true;
							}
							//Else update goodwill and check if user needs to be screened out
							else {

								const validation = progressInfo.validation;

								let reason;
								if (validation) {
									reason = validation.reason;
								}

								const feedbackType = this.returnFeedbackType(screenCode);
								if (screenCode && feedbackType !== 'thankyou') {
									this.handleSurveyModuleTermination(fetchedGroup, screenCode, feedbackType, question, reason);
									return true;
								}

								if (questionSetIndex !== nextQuestionSetIndex) {

									let action = 'inVideoSurvey';
									if (questionSetIndex === this.maximumQuestionSetIndex) {
										action = 'postrollList';
									}

									updatedGoodWill = SURVEY_VALIDATIONS.returnUpdatedGoodwill(updatedGoodWill, action, isTester);
								}

								const userTerminate = SURVEY_VALIDATIONS.checkTerminateUser(screenCode, updatedGoodWill, reason);

								if (userTerminate) {

									const userTerminateScreenCode = userTerminate.screen_code;
									const disableBadResponseTerminate = targetParams.br === '0';
									const submitTermination = userTerminateScreenCode !== BAD_RESPONDANT_CODE || (userTerminateScreenCode === BAD_RESPONDANT_CODE && !disableBadResponseTerminate);

									if (submitTermination) {
										reason = userTerminate.reason;
										this.handleSurveyModuleTermination(fetchedGroup, userTerminateScreenCode, 'terminate', question, reason);
										return true;
									}
								}

								nextQuestionIndex = progressInfo.questionIndex;

								const progressSelectedPaths = progressInfo.selectedPaths;
								if (progressSelectedPaths && progressSelectedPaths.length) {
									pathTracker = pathTracker.concat(
										progressSelectedPaths.filter((path) => !pathTracker.includes(path))
									);
								}

								const progressSelectedAnswers = progressInfo.selectedAnswers;
								if (progressSelectedAnswers && Array.isArray(progressSelectedAnswers) && progressSelectedAnswers.length) {
									answerTracker = answerTracker.concat(progressSelectedAnswers);
								}
							}
						}
					}
				});

				if (interrupt) {
					return;
				}

				isResumingStudy = true;

				//If the survey module returns the next question set as different than the current one,
				//this means that all the questions from this question set are already anwered.

				const firstQuestion = questions[0];
				const questionSetIndex = firstQuestion.qset_index;

				if (nextQuestionSetIndex !== questionSetIndex) {

					//If the study is a video study, continue playing the video until Portal receives another DISPLAY_SURVEY event
					//from the Portal Player to display the questions of the next question set.
					if (isVideoStudy(fetchedGroup)) {
						this.continuePlayingVideo();
					}
					else {
						willShowQuestion = true;
					}
				}
				else {
					willShowQuestion = true;
				}

				newState = {
					...newState,
					answerTracker: answerTracker,
					questionIndex: nextQuestionIndex,
					questionSetIndex: nextQuestionSetIndex
				};

				this.goodwill = updatedGoodWill;
			}
		}

		newState.isResumingStudy = isResumingStudy;

		if (!isResumingStudy) {

			willShowQuestion = true;

			const firstQuestion = questions[0];
			const questionSetIndex = firstQuestion.qset_index;

			newState = {
				...newState,
				questionIndex: 0,
				questionSetIndex: questionSetIndex
			};
		}

		if (willShowQuestion) {

			newState.isSurveyActive = true;

			this.startUserActiveTimer();

			//Lock the orientation to portrait when a question shows
			if (DeviceHelper.isMobile) this.lockOrientation(PORTRAIT_ORIENTATION);

			//Enable vertical scrolling when a survey shows
			const iframes = document.getElementsByTagName('iframe');
			if (iframes && iframes.length) {
				const playerIframe = iframes[0];
				if (playerIframe) {
					playerIframe.style.display = 'none';
				}
			}

			const pilotlyPlayerDivs = document.getElementsByClassName('pilotly-player');
			if (pilotlyPlayerDivs && pilotlyPlayerDivs.length) {
				const pilotlyPlayerDiv = pilotlyPlayerDivs[0];
				if (pilotlyPlayerDiv) {
					pilotlyPlayerDiv.style.overflowY = 'scroll';
				}
			}
		}
		else {
			newState.isSurveyActive = false;
		}

		this.setState(newState);
	}

	updateUser = async payload => {
		const {
			panel_segment,
			group_id,
			state,
			time
		} = payload;

		Logger.log(this.constructor.name, "updateUser()", "Media State " + state, payload);

		try {
			const user_id = this.props.currentUser.user_id;
			const user_token = auth.getToken();
			const result = await updateUserContentProfile(payload, user_token);
			const userSubResult = await updateUserSubscription(user_id, user_token, { group_id });

			if ((result && !result.success && result.message === 'invalid_user_profile')
				|| ((userSubResult && !userSubResult.success))
			) {
				Logger.log(this.constructor.name, "updateUser() eject user", result, userSubResult);
				const data = { message: { eventCall: 'INVALID_USER_PROFILE', params: { timecode: time } } };
				this.handleAnalyticsEvent(data);

				// log ejection in google analytics
				let groupInfo = { group_id: group_id, panel_segment: panel_segment };
				this.handleTermination(groupInfo, EJECT_CODE, result.message);
			}
			else if ((result && !result.success)
				|| (userSubResult && !userSubResult.success)
			) {
				// log error in google analytics
				Logger.log(this.constructor.name, "updateUser() user error", result, userSubResult);
				const data = { message: { eventCall: 'USER_CONTENT_PROFILE_ERROR', params: { timecode: time } } };
				this.handleAnalyticsEvent(data);
			}
			else if ((result && result.success)
				&& (userSubResult && userSubResult.success)) {
				Logger.log(this.constructor.name, "updateUser() USER_CONTENT_PROFILE", result, "USER_SUBSCRIPTION", userSubResult);
			}
		}
		catch (error) {
			if (error && error.code === "user_id_incorrect") {
				Logger.error(this.constructor.name, "updateUser() updateUserContentProfile user_id_incorrect", error);

				let groupInfo = { group_id: group_id, panel_segment: panel_segment };
				this.handleTermination(groupInfo, EJECT_CODE, "user_id_incorrect");
			}
			else {
				Logger.error(this.constructor.name, "updateUser()", "updateUserContentProfile error", error);
				throw new Error(error);
			}
		}
	}

	// parent received a message from iframe
	onReceiveMessage = (event) => {
		Logger.log(this.constructor.name, "onReceiveMessage()", 'event', event);

		const eventData = event.data;

		if (!eventData) {
			Logger.log(this.constructor.name, "onReceiveMessage()", 'no event data provided with message', eventData);
		}

		const data = typeof eventData === 'string' ? JSON.parse(eventData) : eventData;
		const eventType = data.eventType;
		Logger.log(this.constructor.name, "onReceiveMessage()", 'received an event', data);
		Logger.log(this.constructor.name, "onReceiveMessage()", 'event type', eventType);

		switch (eventType) {
			case ("interactiveState"): {
				Logger.log(this.constructor.name, "onReceiveMessage()", 'Handling InteractiveState', data);
				this.handleInteractiveState(data);
				break;
			}
			case ("mediaState"): {
				Logger.log(this.constructor.name, "onReceiveMessage()", 'Handling MediaState', data);
				this.handleMediaState(data);
				break;
			}
			case ("playerState"): {
				Logger.log(this.constructor.name, "onReceiveMessage()", 'Handling PlayerState', data);
				this.handlePlayerState(data);
				break;
			}
			case ("analyticsEvent"): {
				Logger.log(this.constructor.name, "onReceiveMessage()", 'Handling Analytics Event', data);
				this.handleAnalyticsEvent(data);
				break;
			}
		}
	};

	handleAnalyticsEvent = data => {
		const message = data.message;
		const userId = this.props.currentUser && this.props.currentUser.user_id ? this.props.currentUser.user_id : "";
		const platform = this.getDeviceType();
		const event = message ?
			(MediaPlayerStatusEvent[message.eventCall] ?
				MediaPlayerStatusEvent[message.eventCall]
				:
				"MediaPlayer_" + message.eventCall)
			:
			"MediaPlayer_NullMessage";

		let mediaStatus = new MediaPlayerStatus(event, userId, platform, false, false, true);

		/* Google analytics event */
		for (let key in message.params) {
			mediaStatus[key] = message.params[key];
		}

		mediaStatus.view = 'player';

		mediaStatus.gaTracker = this.props.tracker;
		this.props.globalAnalyticsAdapter.reportAnalyticsEvent(mediaStatus);
	}

	handleDisplaySurvey(hintTime) {
		const questions = this.questionSetMapHintTime[hintTime];
		if (questions && questions.length) {
			this.resumeStudy(questions);
		}
	}

	handleMediaState = (data) => {
		const message = data.message;

		const mediaState = message.state;
		const playerTime = message.time;
		const userId = (message.hasOwnProperty('user_id')) ? message.user_id : null;

		const { fetchedEpisode, fetchedGroup } = this.props;
		const episode_id = fetchedEpisode ? fetchedEpisode._id : null;
		const params = this.getParams();
		const { group_id, panelSegmentIndex } = params;

		const payload = {
			browser: DeviceHelper.browser,
			title: fetchedGroup.title,
			panel_segment: panelSegmentIndex,
			group_id,
			device_id: '',
			device: DeviceHelper.device,
			episode_id
		};

		//:NOTE:: unsure why this switch case exists. could place these params above in payload directly
		switch (mediaState) {
			//user_id is what actually made it to the iframe (is also being used in watermark)
			//passing this back on media state in order to verfiy it hasnt been manipulated in anyway
			case PCORE_EVENT.START:
			case PCORE_EVENT.UPDATE: {

				Logger.log(this.constructor.name, "handleMediaState()", mediaState, playerTime);

				payload.state = mediaState;
				payload.time = playerTime;
				payload.user_id = userId;

				if (!helpers.isGroupSurveyOnly(this.props.fetchedGroup)) this.updateUser(payload);

				break;
			}
			case PCORE_EVENT.INFO:
				if (DeviceHelper.isMobile) {
					//Lock the orientation to landscape when the intro video begins playing
					const introVideoActive = message.introVideoActive;

					if (introVideoActive && !this.isOrientationLocked) {

						//For Google Chrome on Android, when the fullscreen mode is enabled, the Skip button isn't showing when the intro video plays.
						//So exit fullscreen mode to show the Skip button and change the required orientation to landscape.
						//But don't lock the orientation to not enable fullscreen mode again.
						//This way if the current orientation isn't landscape, show the message instructing the user to rotate the device.
						if (DeviceHelper.isAndroid && DeviceHelper.isChrome) {

							exitFullscreen();

							this.listenToOrientationChange();

							this.setState({ requiredOrientation: LANDSCAPE_ORIENTATION });

							const currentOrientation = this.getCurrentOrientation();
							if (currentOrientation !== LANDSCAPE_ORIENTATION) {
								this.handleOrientationChanged(currentOrientation);
							}
						}
						else {
							this.lockOrientation(LANDSCAPE_ORIENTATION);
						}
					}
				}
				break;
			case PCORE_EVENT.TIME_UPDATE_STATE:
				if (DeviceHelper.isMobile) {
					//Save timecode to used when the user answers the tuneout question on mobile
					const timecode = message.time;
					this.timecode = timecode;

					//Lock the orientation to landscape when the study video begins playing
					const isSurveyActive = this.state.isSurveyActive;
					if (!isSurveyActive) {
						const requiredOrientation = this.state.requiredOrientation;
						if (requiredOrientation !== LANDSCAPE_ORIENTATION) {
							this.lockOrientation(LANDSCAPE_ORIENTATION);
						}
					}
				}

				break;
		}
	}

	handleInteractiveState = (data) => {
		Logger.log(this.constructor.name, "handleInteractiveState()", data);

		const message = data.message;
		const state = message.state;
		const event = message.event;
		Logger.log(this.constructor.name, "handleInteractiveState()", 'HANDLING MESSAGE STATE', state);
		Logger.log(this.constructor.name, "handleInteractiveState()", 'HANDLING MESSAGE EVENT', event);

		//:HOWIE:: We should probably shift POST ANSWER to be a messageEvent so everything falls into the same switch
		if (event) {

			switch (event) {
				//:NOTE:: anything that terminates should come with a screencode 
				//qt (question terminate) st (sessiontimeout) ql (question logic)  br (bad respondant)  at (aid termination logic ~not really in use)
				//the error code column could be used to provide further detail around the screencode
				//skipped-questions   speed   bad-openend
				case PCORE_EVENT.TERMINATE: {
					this.handleTerminate(data.message);
					break;
				}
				case PCORE_EVENT.BAD_RESPONDANT: {
					this.handleBadRespondants(data.message);
					break;
				}
				//:NOTE:: unsure what this does other than show a prompt, does it terminate or do data quality redirect?
				case PCORE_EVENT.FINAL_SUBMISSION_FAIL: {
					this.handleFailFinalSubmission();
					break;
				}
				case PCORE_EVENT.DISPLAY_SURVEY: {
					this.setState({ receivedDisplaySurveyEvent: true });
					const hintTime = message.hint_time;
					this.handleDisplaySurvey(hintTime);
					break;
				}
				case PCORE_EVENT.QUESTION_SET_START: {
					this.startUserActiveTimer();
					//Lock the orientation to portrait when a question shows
					if (DeviceHelper.isMobile) {
						this.setState({ isSurveyActive: true });
						this.lockOrientation(PORTRAIT_ORIENTATION);
					}
					break;
				}
				case PCORE_EVENT.QUESTION_SET_COMPLETE: {
					this.setState({ isSurveyActive: false });
					this.stopUserActiveTimer();
					break;
				}
				case PCORE_EVENT.TUNEOUT: {
					this.handleTuneout();
					break;
				}
				case PCORE_EVENT.MAXDIFF_VERSION_REQUEST: {
					this.handleMaxDiffVersionRequest(data);
					break;
				}
				case PCORE_EVENT.FOLLOW_UP_REQUEST: {
					this.handleFollowUpRequest(data);
					break;
				}
			}
		}

		switch (state) {
			case (PCORE_EVENT.POST_ANSWER):{
				this.postPanelResponse(data.message.qinfo);
				break;
			}
			case (PCORE_EVENT.POST_SCENE): {
				this.postSceneResponse(data.message);
				break;
			}
		}
	}

	handleMaxDiffVersionRequest = async (data) => {
		Logger.log(this.constructor.name, "handleMaxDiffVersionRequest()", data);
		// configure this method to use an api request for the msxdiff that the user will see in the survey disply
		const { qid } = data.message;

		const user_token = auth.getToken();

		const params = {
			qid,
			panel_segment_index: parseInt(this.props.targetParams.panelSegmentIndex),
			group_id: this.props.fetchedGroup._id,
			survey_id: this.props.fetchedPanel._id
		};
		// this is a temporary function that returns maxdiff versions too save in the state.
		try {
			const assignedVersion = await initUserQuestionAssignment(this.props.currentUser.user_id, user_token, params);
			
			const version_id = assignedVersion.data.version_id;
			const versionExists = this.state.assignedQuestionVersions.some(
				version => version.version_id === version_id
			);
			
			const assignedQuestionVersions = versionExists 
				? this.state.assignedQuestionVersions
				: [...this.state.assignedQuestionVersions, {
					qid,
					question_type: 'maxdiff', // this is temporary as we'll have to scale if we'd like to expand this to multiple question types
					version_id: version_id
				}];
			this.setState({ assignedQuestionVersions }, () => {
				this.sendMessage({
					event: PCORE_EVENT.MAXDIFF_VERSION_RESPONSE,
					value: assignedVersion.data.version_id
				});
			});
		} catch (error) {
			Logger.error(this.constructor.name, "handleMaxDiffVersionRequest()", "initUserQuestionAssignment error", error);
			this.setState({ assignedQuestionVersions: null, feedbackType: 'error', error: error });
		}
	}

	async handleSurveyModuleTermination(group, screenCode, feedbackType, question, reason) {
		Logger.log(this.constructor.name, "handleSurveyModuleTermination(): SCREEN:", screenCode, "FEEDBACK", feedbackType, "QID:", question.qid, "QTYPE:", question.type);

		const { currentUser, fetchedGroup, targetParams } = this.props;

		const disableBadResponseTerminate = targetParams.br === '0';
		const submitTermination = screenCode !== BAD_RESPONDANT_CODE || (screenCode === BAD_RESPONDANT_CODE && !disableBadResponseTerminate);

		if (submitTermination) {
			let panelSegmentIndex = targetParams.panelSegmentIndex ?
				targetParams.panelSegmentIndex.toString()
				:
				targetParams.ps ?
					targetParams.ps
					:
					"0";

			let user_token = auth.getToken();
			
			const params = {
				user_token,
				group_id: group._id,
				panel_segment: panelSegmentIndex,
				user_id: currentUser.user_id,
				screen_code: screenCode,
				reason: reason ? reason : 'unknown',
				screen_qid: question.qid
			};

			const isTester = TargetProcess.isTester(targetParams);

			if (isTester) {
				params.test = targetParams.test;
			}

			if (fetchedGroup && fetchedGroup.video_id) {
				params.video_id = fetchedGroup.video_id;
			}

			terminateUser(params).then(result => {
				Logger.log(this.constructor.name, "handleSurveyModuleTermination()", result);
				this.setFeedbackType({ feedbackType });
			}).catch((error) => {
				Logger.log(this.constructor.name, "handleSurveyModuleTermination()", 'Reporting Termination FAILED', error);
				this.setFeedbackType({feedbackType: 'error', error: error});
			});
		}
	}

	handleTermination = async (groupInfo, screenCode, screenReason) => {
		Logger.log(this.constructor.name, "handleTermination():", groupInfo);
		const { currentUser, fetchedGroup, targetParams } = this.props;
		const userToken = auth.getToken();
		const psIndex = targetParams.panelSegmentIndex
			? targetParams.panelSegmentIndex.toString()
			: targetParams.ps
				? targetParams.ps
				: (groupInfo && groupInfo.panel_segment ? groupInfo.panel_segment : "0");

		const group = groupInfo || { group_id: targetParams.gid, panel_segment: psIndex };
		const payload = {
			group_id: group.group_id,
			panel_segment: group.panel_segment,
			screen_code: screenCode,
			reason: screenReason,
			user_id: currentUser.user_id,
			user_token: userToken
		};

		const isTester = TargetProcess.isTester(this.props.targetParams);
		if (isTester) payload.test = targetParams.test;
		if (fetchedGroup && fetchedGroup.video_id) payload.video_id = fetchedGroup.video_id;

		try {
			const result = await terminateUser(payload);

			Logger.log(this.constructor.name, "handleTermination()", "terminateUser result", result);

			if (screenCode === BAD_RESPONDANT_CODE || screenCode === SESSION_LENGTH_CODE) {
				this.setFeedbackType({ feedbackType: 'dataquality' });
			}
			else {
				this.setFeedbackType({ feedbackType: 'terminate' });
			}
		}
		catch (error) {
			Logger.error(this.constructor.name, "handleTermination()", "terminateUser error", error);
			this.setFeedbackType({ feedbackType: 'error', error: error });
		}
	}

	handleTerminate = (data) => {
		Logger.log(this.constructor.name, "handleTerminate()", 'TERMINATING', data);
		this.handleTermination(data.groupInfo, data.screen_code, data.reason);
	}

	handleBadRespondants = async (data) => {
		const user_token = auth.getToken();
		const user_id = this.props.currentUser.user_id;

		Logger.log(this.constructor.name, "handleBadRespondants()", 'REDIRECT USER TO BAD DATA QUALITY OR TERMIANTE', data);

		try {
			const result = await penalizeUser(user_token, user_id, 1);

			if (result) {
				Logger.log(this.constructor.name, "handleBadRespondants()", 'PENALIZE SUCCESSFUL, user_id:', user_id);
				let group_id = this.props.targetParams ? this.props.targetParams.gid : this.props.fetchedGroup._id;
				let psIndex = this.props.targetParams ? this.props.targetParams.panelSegmentIndex.toString() : this.props.fetchedGroupPanelSegment.toString();
				let groupInfo = { group_id: group_id, panel_segment: psIndex };
				this.handleTermination(groupInfo, data.screen_code, data.reason);
			}
			else {
				Logger.log(this.constructor.name, "handleBadRespondants()", 'PENALIZE FAILED');
			}
		}
		catch (error) {
			Logger.log(this.constructor.name, "handleBadRespondants()", 'PENALIZE FAILED', error);
			this.setFeedbackType({ feedbackType: 'error', error: error });
		}

	}

	async handleTuneout() {
		if (this.isShowingTuneoutModal) {
			this.isShowingTuneoutModal = false;
		}
		else {
			this.isShowingTuneoutModal = true;

			Logger.log(this.constructor.name, "handleTuneout()", 'HANDLE TUNEOUT');

			await exitFullscreen();

			this.sendToggleLockMessage(true, true);

			const tuneoutTitle = this.props.intl.formatMessage(localeMessages.tuneout_title);
			const tuneoutMessage = this.props.intl.formatMessage(localeMessages.tuneout_message);

			this.openModal({ title: tuneoutTitle, message: tuneoutMessage, modalType: PCORE_EVENT.TUNEOUT });
		}
	}

	handleFailFinalSubmission = () => {
		Logger.log(this.constructor.name, "handleFailFinalSubmission()", 'Some user responses were not received, need to let user know their internet down');

		const internet_connectivity_title = this.props.intl.formatMessage(localeMessages.internet_connectivity_title);
		const internet_connectivity_message = this.props.intl.formatMessage(localeMessages.internet_connectivity_message);

		this.openModal({
			title: internet_connectivity_title, message: internet_connectivity_message, modalType: PCORE_EVENT.FINAL_SUBMISSION_FAIL
		});
	}

	retryFinalDataSubmission = () => {
		Logger.log(this.constructor.name, "retryFinalDataSubmission()", 'Request the player to try submission / closing process again');

		const methodCallMessage = { event: PCORE_EVENT.METHOD_CALL, method: 'reSubmitFinalAnswers', value: {} };
		this.sendMessage(methodCallMessage);
	}

	confirmTuneOut() {

		Logger.log(this.constructor.name, "confirmTuneOut()");

		this.closeModal();

		// Mobile: Show the tuneout question as a open ended question component from the survey module on mobile
		if (DeviceHelper.isMobile) {
			this.showTuneoutQuestion();
		}
		// Desktop: Send event to Portal Player to display the tuneout question using it's own open ended question component
		else {
			this.isShowingTuneoutQuestion = true;
			this.sendTuneOutMessage();
		}
	}

	closePlayer = () => {
		const { targetParams } = this.props;
		/* 
			Close player should only be hit when the back arrow is hit in the iframe
			that is why we send the user to the home view.
			The back arrow is only displayed if you are a Pilotly user and not coming
			from a partner.
		*/

		Logger.log(this.constructor.name, "closePlayer()", this.props);

		if (targetParams && (targetParams.partner || targetParams.dl)) return;

		console.log(this.constructor.name, "CLOSE PLAYER ENABLED");
		this.goHome();
	}

	closeModal() {
		this.isShowingTuneoutModal = false;
		window.$('#pilotly-player-modal').modal('close');
	}

	openModal({ title, message, modalType }) {
		this.setState({ modalTitle: title, modalMessage: message, modalType }, () => {
			window.$('#pilotly-player-modal').modal({onCloseEnd: this.onCloseModal});
			window.$('#pilotly-player-modal').modal('open');
		});
	}

	handlePlayerState = (data) => {
		const message = data.message;
		const event = message.event;
		const source = message.source;
		Logger.log(this.constructor.name, "handlePlayerState()", event, PCORE_EVENT);

		switch (event) {
			case PCORE_EVENT.CLOSE_PLAYER: {
				this.closePlayer('close');
				break;
			}
			case PCORE_EVENT.COMPLETE: {
				this.handleQuestionVersionComplete();
				this.handleComplete();
				break;
			}
			case PCORE_EVENT.READY: {
				this.handleReady();
				break;
			}
			case PCORE_EVENT.SETUP_ERROR:
			case PCORE_EVENT.ERROR: {
				this.handleError(data.message);
				break;
			}
		}
	}

	handleError = (message) => {
		Logger.log(this.constructor.name, "handleError()", message);

		this.notifyError(message);

		if (message && message.survey_status == "active") {
			return;
		}

		this.setFeedbackType({ feedbackType: 'error' });
	}

	handleQuestionVersionComplete = async () => {
		if (!this.state.assignedQuestionVersions || this.state.assignedQuestionVersions.length === 0) {
			Logger.log(this.constructor.name, "handleQuestionVersionComplete() - No MaxDiff questions, skipping");
			return;
		}

		Logger.log(this.constructor.name, "handleQuestionVersionComplete()");
		const user_id = this.props.currentUser.user_id;
		const user_token = auth.getToken();
		const params = {
			group_id: this.props.fetchedGroup._id,
			panel_segment_index: parseInt(this.props.targetParams.panelSegmentIndex),
			survey_id: this.props.fetchedPanel._id,
			complete: 1,
			versions: this.state.assignedQuestionVersions,
		};

		try {
			await updateUserQuestionAssignment(user_id, user_token, params);
		} catch (error) {
			Logger.error(this.constructor.name, "handleQuestionVersionComplete()", "updateUserQuestionAssignment error", error);
		}
	}

	async handleComplete() {
		Logger.log(this.constructor.name, "handleComplete()");
		const { fetchedGroup } = this.props;
		this.handlingComplete = true;
		if (!this.submittingResponse) {
			if (DeviceHelper.isMobile) await this.completeUserContentProfile();
			this.setFeedbackType({
				feedbackType: fetchedGroup.data_type === LINKED_MODERATED_STUDY ? 'waitingroom' : 'thankyou'
			});
		}
	}

	// iframe has loaded
	onReady = () => {
		Logger.log(this.constructor.name, "onReady()");
	};

	goHome = () => {
		Logger.log(this.constructor.name, "goHome()");
		this.props.history.push('/home');
	}

	sendToggleLockMessage(state, isShowingTuneoutModal, preventPausePlay) {
		Logger.log(this.constructor.name, "sendToggleLockMessage()", 'TOGGLE LOCK PLAYER');
		let toggleLockMessage = { event: PCORE_EVENT.TOGGLE_LOCK, isShowingTuneoutModal, preventPausePlay };
		if (state || state === false) {
			toggleLockMessage.state = state;
		}
		this.sendMessage(toggleLockMessage);
	}

	handleTuneOutNo() {

		Logger.log(this.constructor.name, "handleTuneOutNo()");

		this.closeModal();

		this.sendToggleLockMessage(false, false);

		if (DeviceHelper.isMobile) this.lockOrientation(LANDSCAPE_ORIENTATION);
	}

	//:NOTE:: relays a follow-up request from the player to the agent service and sends the
	//outcome back. The credential and service URL stay on this side; the player never talks
	//to the agent directly.
	//
	//This ALWAYS replies, including on error. The player runs its own deadline as a backstop,
	//but a handler that can return silence leaves a respondent watching a spinner until that
	//deadline expires — see handleMaxDiffVersionRequest, whose catch sends nothing.
	//
	//requestId is echoed so the player can match a reply to its request. Beyond correlation
	//it also guarantees each message object differs, which matters because IframeComm only
	//re-sends when postMessageData changes by reference — two identical replies in a row
	//would otherwise be dropped, exactly what a chain of declines produces.
	//:NOTE:: the one place a follow-up request is actually made. Both callers come through
	//here — the iframe player over postMessage, and the survey module through a callback
	//prop — so the keys are stamped and the failure shape is decided once rather than twice.
	askFollowUpAgent = async (payload) => {
		//:NOTE:: studyKey, respondentKey and language are stamped HERE rather than taken from
		//the caller. All three are already authoritative on this side — the same values used
		//for every panel response — so the agent labels a call from server-held state instead
		//of trusting whatever was supplied. sequenceKey comes from the caller, since only it
		//knows which chain a call belongs to.
		//
		//language overwrites whatever a caller sent, and both callers do send one. Neither
		//knows better than this side: the iframe player only has the code handed to it in its
		//own URL, and the survey module only has the prop this component passed it — both of
		//which are getLanguage(), the interface language, browser-derived on any link without
		//?lang=. The agent needs the language the study is fielded in.
		const params = this.getParams();
		const request = Object.assign({}, payload, {
			studyKey: String((params && params.group_id) || ''),
			respondentKey: String((this.props.currentUser && this.props.currentUser.user_id) || ''),
			language: this.getStudyLanguage()
		});

		try {
			return await requestFollowUp(request);
		} catch (error) {
			//:NOTE:: requestFollowUp resolves on every path, so this is belt and braces. It
			//exists so an unforeseen throw still produces a result rather than a hung
			//respondent.
			Logger.error(this.constructor.name, "askFollowUpAgent()", 'unexpected failure', error);
			return { outcome: 'failed', followUp: null, meta: null };
		}
	}

	//:NOTE:: the survey module's entry point. It has no HTTP client and no postMessage
	//channel of its own, so it hands us the payload and awaits the outcome directly. The
	//agent groups every call for one original answer by sequenceKey, and the module does
	//not send one, so the key is minted here once per chain and reused for the rest of it.
	//A chain starts on followUpIndex 1, or when the original answer changes — a respondent
	//can answer the same question again, and that is a different sequence. The payload's
	//own key still wins, so a later survey release can take this over without a host change.
	handleSurveyFollowUpRequest = async (payload) => {
		const answer = payload && payload.answer;
		const isNewChain = !this.surveyFollowUpChain
			|| (payload && payload.followUpIndex) === 1
			|| this.surveyFollowUpChain.answer !== answer;

		if (isNewChain) {
			this.surveyFollowUpChain = {
				answer: answer,
				key: 'sq_' + Date.now() + '_' + Math.floor(Math.random() * 100000)
			};
		}

		const sequenceKey = (payload && payload.sequenceKey) || this.surveyFollowUpChain.key;

		const result = await this.askFollowUpAgent(Object.assign({}, payload, { sequenceKey }));

		Logger.log(this.constructor.name, "handleSurveyFollowUpRequest()", 'outcome', result.outcome, sequenceKey);

		return result;
	}

	handleFollowUpRequest = async (data) => {
		const message = (data && data.message) || {};
		const requestId = message.requestId;

		Logger.log(this.constructor.name, "handleFollowUpRequest()", 'requesting a follow-up', requestId);

		const result = await this.askFollowUpAgent(message.payload || {});

		Logger.log(this.constructor.name, "handleFollowUpRequest()", 'outcome', result.outcome, requestId);

		this.sendMessage({
			event: PCORE_EVENT.FOLLOW_UP_RESPONSE,
			requestId: requestId,
			value: result
		});
	}

	sendMessage = (message) => {
		Logger.log(this.constructor.name, "sendMessage()");
		this.setState({ postMessageData: message });
	}

	async handleUserActive() {
		const params = this.getParams();
		const groupId = params.group_id;
		const panelSegmentIndex = params.panelSegmentIndex;
		const userId = this.props.currentUser.user_id;
		const userToken = auth.getToken();
		try {
			const userSubResult = await updateUserSubscription(userId, userToken, { group_id: groupId });
			if (userSubResult && !userSubResult.success) {
				Logger.log(this.constructor.name, "handleUserActive() eject user", userSubResult, userSubResult);
				const data = { message: { eventCall: 'UPDATE_USER_SUBSCRIPTION_ERROR', params: { groupId: groupId, panelSegmentIndex: panelSegmentIndex, userId: userId } } };
				this.handleAnalyticsEvent(data);
			}
			else {
				Logger.log(this.constructor.name, "handleUserActive() UPDATE_USER_SUBSCRIPTION", userSubResult);
			}
		}
		catch (error) {
			if (error && error.code === "user_id_incorrect") {
				Logger.error(this.constructor.name, "handleUserActive() updateUserSubscription user_id_incorrect", error);
				const groupInfo = { group_id: groupId, panel_segment: panelSegmentIndex };
				this.handleTermination(groupInfo, EJECT_CODE, "user_id_incorrect");
			}
			else {
				Logger.error(this.constructor.name, "handleUserActive()", "updateUserSubscription error", error);
				throw new Error(error);
			}
		}
	}

	startUserActiveTimer() {
		if (!this.userActiveTimer) {
			this.userActiveTimer = setInterval(this.handleUserActive, 5000);
		}
	}

	stopUserActiveTimer() {
		if (this.userActiveTimer) {
			clearInterval(this.userActiveTimer);
			this.userActiveTimer = null;
		}
	}

	getModalBtns = (type) => {

		switch (type) {

			case (PCORE_EVENT.TUNEOUT):
				return (
					<div>
						<Button onClick={this.handleTuneOutNo} className='tuneout-modal-btn no-modal-btn' style={{ marginRight: '8px' }}>
							<FormattedMessage
								id="player.modal.button.no"
								defaultMessage={`No`}
							/>
						</Button>
						<Button onClick={this.confirmTuneOut} className='tuneout-modal-btn yes-modal-btn'>
							<FormattedMessage
								id="player.modal.button.yes"
								defaultMessage={`Yes`}
							/>
						</Button>
					</div>
				);

			case (PCORE_EVENT.FINAL_SUBMISSION_FAIL):
				return (
					<div>
						<Button onClick={this.retryFinalDataSubmission} className='yes-modal-btn'>
							<FormattedMessage
								id="player.button.retry_submission"
								defaultMessage={`Retry Submission`}
							/>
						</Button>
					</div>
				);
		}
	}

	async setFeedbackType(updates) {
		await this.unlockOrientation();
		this.setState(updates);
	}

	handleReady() {

		if (!this.isPlayerReady) {

			//Lock the orientation to landscape when the study is resuming
			if (DeviceHelper.isMobile) {

				const { fetchedGroup, fetchedEpisode, currentUser } = this.props;
				const resumeTimecode = isVideoStudy(fetchedGroup) ? getCompletedTimecode(fetchedGroup, fetchedEpisode, currentUser) : 0;

				if (resumeTimecode > 0) {
					this.lockOrientation(LANDSCAPE_ORIENTATION);
				}
			}

			this.isPlayerReady = true;

			this.setState({ showLoader: false });
		}
	}

	fullscreenChanged() {

		if (DeviceHelper.isMobile) {

			const isFullscreenEnabled = getIsFullscreenEnabled();

			if (isFullscreenEnabled && !this.isOrientationLocked) {
				const requiredOrientation = this.state.requiredOrientation;
				this.lockOrientation(requiredOrientation);
			}
			//When the user exits fullscreen mode when the orientation is already locked,
			//the orientation is unlocked automatically
			else if (this.isOrientationLocked) {
				this.isOrientationLocked = false;
			}
		}
	}

	getCurrentOrientation() {

		if (DeviceHelper.isMobile) {

			if (!this.landscapeOrientationMediaQuery) {
				this.landscapeOrientationMediaQuery = window.matchMedia('(orientation: landscape)');
			}

			if (this.landscapeOrientationMediaQuery.matches) {
				return LANDSCAPE_ORIENTATION;
			}

			return PORTRAIT_ORIENTATION;
		}

		return null;
	}

	handleOrientationChanged(currentOrientation) {

		//Render the div with the text and pause the video when the orientation IS NOT the required orientation
		//and hide it and play the video when the orientation IS the required orientation

		const message = { event: PCORE_EVENT.METHOD_CALL, method: 'togglePlay', value: PCORE_EVENT.PAUSE };

		const {
			isSurveyActive,
			requiredOrientation,
			tuneoutQuestion
		} = this.state;

		if (currentOrientation === requiredOrientation) {

			const playVideo = !(this.isShowingTuneoutModal || this.isShowingTuneoutQuestion || isSurveyActive || tuneoutQuestion);

			if (playVideo) message.value = PCORE_EVENT.PLAY;

			if (!this.isOrientationLocked && requiredOrientation === LANDSCAPE_ORIENTATION) {
				this.lockOrientation(LANDSCAPE_ORIENTATION);
			}
		}
		// Close tuneout modal to show the rotate device message
		else if (this.isShowingTuneoutModal) {
			this.closeModal();
			this.sendToggleLockMessage(false, false, true);
			return;
		}

		this.sendMessage(message);
	}

	listenToFullscreenChange() {
		//When locking the orientation, add a listener to identify when the user exits fullscreen mode and enters fullscreen mode again.
		//This way we can try to lock the orientation to landscape again when the user enters fullscreen mode again.
		//Add event listener to the events fullscreenchange and webkitfullscreenchange.
		//The event webkitfullscreenchange is supported by older versions of Google Chrome
		//and the event fullscreenchange is supported by newer versions of Google Chrome
		//so for the fullscreen listener to be backwards compatible, add event listeners for both events.
		document.removeEventListener('fullscreenchange', this.fullscreenChanged);
		document.removeEventListener('webkitfullscreenchange', this.fullscreenChanged);
		document.addEventListener('fullscreenchange', this.fullscreenChanged);
		document.addEventListener('webkitfullscreenchange', this.fullscreenChanged);
	}

	listenToOrientationChange() {

		if (DeviceHelper.isMobile) {

			if (!this.landscapeOrientationMediaQuery) {
				this.landscapeOrientationMediaQuery = window.matchMedia('(orientation: landscape)');
			}

			this.landscapeOrientationMediaQuery.removeEventListener('change', this.orientationChanged);
			this.landscapeOrientationMediaQuery.addEventListener('change', this.orientationChanged);
		}
	}

	async lockOrientation(targetOrientation) {

		if (DeviceHelper.isMobile && targetOrientation) {

			let isOrientationLocked = this.isOrientationLocked;

			//The Web API window.screen.orientation.lock() isn't currently available in Safari on iOS. Date: 29th April, 2024.
			//https://caniuse.com/mdn-api_screenorientation_lock
			//https://developer.mozilla.org/en-US/docs/Web/API/ScreenOrientation/lock

			if (isOrientationLockAvailable()) {
				try {

					//Just enable fullscreen and lock orientation when the required orientation is landscape.
					//Question components are not working in fullscreen mode.
					if (targetOrientation === PORTRAIT_ORIENTATION) {
						await this.unlockOrientation();
						isOrientationLocked = false;
					}
					else {
						const iframe = document.getElementsByTagName('iframe')[0];

						//Locking the orientation just works when the browser is in fullscreen mode.
						const isFullscreenEnabled = await enableFullscreen(iframe);
						if (isFullscreenEnabled) {
							await window.screen.orientation.lock(targetOrientation);
							isOrientationLocked = true;
						}

						this.listenToFullscreenChange();
					}
				}
				catch (e) {
					Logger.log(`lockOrientationTo() error - targetOrientation = ${targetOrientation}`, e);
				}
			}

			this.isOrientationLocked = isOrientationLocked;

			this.listenToOrientationChange();

			this.setState({ requiredOrientation: targetOrientation }, () => {
				//IF the operation to lock the orientation failed
				//AND the current orientation is different from the required orientation,
				//THEN show the message instructing the user to rotate the device.
				if (!isOrientationLocked) {
					const currentOrientation = this.getCurrentOrientation();
					if (targetOrientation !== currentOrientation) {
						this.handleOrientationChanged(currentOrientation);
					}
				}
			});
		}
	}

	orientationChanged(event) {
		let currentOrientation = PORTRAIT_ORIENTATION;

		if (event.matches) {
			currentOrientation = LANDSCAPE_ORIENTATION;
		}

		this.handleOrientationChanged(currentOrientation);
	}

	async unlockOrientation() {
		if (DeviceHelper.isMobile) {

			document.removeEventListener('fullscreenchange', this.fullscreenChanged);
			document.removeEventListener('webkitfullscreenchange', this.fullscreenChanged);

			if (this.landscapeOrientationMediaQuery) {
				this.landscapeOrientationMediaQuery.removeEventListener('change', this.orientationChanged);
				this.landscapeOrientationMediaQuery = null;
			}

			await exitFullscreen();

			if (this.isOrientationLocked) {

				const orientation = window.screen.orientation;
				const unlock = orientation.unlock;

				if (unlock && typeof unlock === 'function') {
					orientation.unlock();
					this.isOrientationLocked = false;
				}
			}
		}
	}

	continuePlayingVideo() {

		this.stopUserActiveTimer();

		const message = {event: 'end_survey_section'};
		this.sendMessage(message);

		this.setState({ isResumingStudy: false, isSurveyActive: false });

		//Disable vertical scrolling when the user completes a survey
		const iframes = document.getElementsByTagName('iframe');
		if (iframes && iframes.length > 0) {
			const playerIframe = iframes[0];
			if (playerIframe) {
				playerIframe.style.removeProperty('display');
			}
		}

		const pilotlyPlayerDivs = document.getElementsByClassName('pilotly-player');
		if (pilotlyPlayerDivs && pilotlyPlayerDivs.length > 0) {
			const pilotlyPlayerDiv = pilotlyPlayerDivs[0];
			if (pilotlyPlayerDiv) {
				pilotlyPlayerDiv.style.overflowY = 'hidden';
			}
		}

		//On mobile, restore the landscape orientation requirement now that the survey is finished
		//(mirrors the portrait lock applied when a survey shows). Setting it here - while the user is
		//still in portrait - shows the rotate-to-landscape prompt and lets the subsequent orientation
		//change resume playback. Relying on the later TIME_UPDATE_STATE tick instead races the user's
		//rotation and leaves the video frozen with no overlay.
		if (DeviceHelper.isMobile) this.lockOrientation(LANDSCAPE_ORIENTATION);
	}

	handleBadRespondents(validation) {
		Logger.log(this.constructor.name, "handleBadRespondents() REASON:", validation.reason, "QID:", validation.qid, "QTYPE:", validation.type);
		if (!validation || !validation.reason) return;

		const { fetchedGroup, targetParams } = this.props;

		Logger.log(this.constructor.name, "processBadRespondant()", validation.reason, this.goodwill);

		const tokens = this.goodwill && this.goodwill.tokens ? this.goodwill.tokens : -1;

		Logger.log(this.constructor.name, "handleBadRespondents() TOKENS:", tokens);
		if (tokens < 1) {
			this.handleTermination(fetchedGroup, BAD_RESPONDANT_CODE, 'terminate', validation, validation.reason);
		}
		else if (tokens > 0) {
			const isTest = TargetProcess.isTester(targetParams);
			this.goodwill = SURVEY_VALIDATIONS.returnUpdatedGoodwill(this.goodwill, 'lose_token', isTest);
			Logger.log(this.constructor.name, "processBadRespondant() let it slide, taking a token, " + (tokens - 1) + " remaining");
		}
	}

	handleProgressUpdate(questionIndex, nextQuestionSetIndex, selectedPaths, selectedAnswers, screenCode, question, questionValidation) {

		const { fetchedGroup, fetchedPanel, targetParams } = this.props;

		let { answerTracker, pathTracker, questionSetIndex } = this.state;

		let reason;
		if (questionValidation) {
			reason = questionValidation.reason;
		}

		const feedbackType = this.returnFeedbackType(screenCode);
		if (screenCode && feedbackType !== 'thankyou') {
			this.handleSurveyModuleTermination(fetchedGroup, screenCode, feedbackType, question, reason);
			return;
		}

		let updatedGoodWill = this.goodwill;

		let validationStatus;
		if (questionValidation) {
			validationStatus = questionValidation.status;
		}

		const isTest = TargetProcess.isTester(targetParams);

		if (validationStatus === 'fail') {
			updatedGoodWill = SURVEY_VALIDATIONS.returnUpdatedGoodwill(updatedGoodWill, 'lose_token', isTest);
		}

		if (questionSetIndex !== nextQuestionSetIndex) {

			let action = 'inVideoSurvey';
			if (questionSetIndex === this.maximumQuestionSetIndex) {
				action = 'postrollList';
			}

			updatedGoodWill = SURVEY_VALIDATIONS.returnUpdatedGoodwill(updatedGoodWill, action, isTest);
		}

		const userTerminate = SURVEY_VALIDATIONS.checkTerminateUser(screenCode, updatedGoodWill, reason);

		if (userTerminate) {

			const userTerminateScreenCode = userTerminate.screen_code;
			const disableBadResponseTerminate = targetParams.br === '0';

			const submitTermination = userTerminateScreenCode !== BAD_RESPONDANT_CODE || (userTerminateScreenCode === BAD_RESPONDANT_CODE && !disableBadResponseTerminate);

			if (submitTermination) {
				reason = userTerminate.reason;
				this.handleSurveyModuleTermination(fetchedGroup, userTerminateScreenCode, 'terminate', question, reason);
				return;
			}
		}

		let newState = {questionIndex: questionIndex, questionSetIndex: nextQuestionSetIndex};

		if (selectedAnswers && Array.isArray(selectedAnswers) && selectedAnswers.length) {
			answerTracker = answerTracker.concat(selectedAnswers);
		}

		newState.answerTracker = answerTracker;

		if (selectedPaths && selectedPaths.length > 0) {
			pathTracker = pathTracker.concat(selectedPaths);
		}

		newState.pathTracker = pathTracker;

		if (screenCode === THANK_YOU || questionSetIndex !== nextQuestionSetIndex) {

			// Survey-only studies have no video to hand control back to. When none of the
			// remaining question sets (from nextQuestionSetIndex onward) have displayable
			// questions — e.g. only the exit survey prompt is left, which the survey module
			// never renders — the study is finished and should complete.
			const isSurveyOnly = helpers.isGroupSurveyOnly(fetchedGroup);
			const hasRemainingDisplayableQuestions = this.hasDisplayableQuestionsFromSet(fetchedPanel, nextQuestionSetIndex);

			if (screenCode === THANK_YOU
				|| nextQuestionSetIndex > this.maximumQuestionSetIndex
				|| (isSurveyOnly && !hasRemainingDisplayableQuestions)
			) {
				this.handleQuestionVersionComplete();
				this.handleComplete();
			}
			else if (nextQuestionSetIndex <= this.maximumQuestionSetIndex && isVideoStudy(fetchedGroup)) {
				this.continuePlayingVideo();
			}
		}

		this.goodwill = updatedGoodWill;

		// The trackers must merge off prevState rather than the values read from
		// this.state at the top of this method. handleTrackerUpdate() reports paths and
		// answers produced by hidden logic questions in this same React batch, so those
		// values are not visible yet and a plain setState here would discard them.
		this.setState((prevState) => {
			const mergedAnswerTracker = prevState.answerTracker.concat(
				(newState.answerTracker || []).filter((aid) => !prevState.answerTracker.includes(aid))
			);
			const mergedPathTracker = prevState.pathTracker.concat(
				(newState.pathTracker || []).filter((path) => !prevState.pathTracker.includes(path))
			);

			return { ...newState, answerTracker: mergedAnswerTracker, pathTracker: mergedPathTracker };
		});
	}

	handleQuestionIndexOverride(updatedQuestionIndex) {
		const newQuestionIndex = updatedQuestionIndex ? updatedQuestionIndex : 0;
		this.setState({questionIndex: newQuestionIndex});
	}

	handleTuneoutAnswer(answer, paths, validation) {
		const tuneoutQuestion = this.state.tuneoutQuestion;
		const language = this.getLanguage();

		this.handlePanelResponse(
			answer,
			null,
			tuneoutQuestion,
			null,
			validation,
			null,
			language
		);

		this.continuePlayingVideo();
		this.setState({ tuneoutQuestion: null });
	}

	returnParams() {
		let group_id = null;
		let panelSegmentIndex = null;

		if (this.props.targetParams && this.props.targetParams.test && parseInt(this.props.targetParams.test) === 1) {
			group_id = this.props.targetParams.gid;
			panelSegmentIndex = this.props.targetParams.panelSegmentIndex || this.props.targetParams.ps || 0;
			panelSegmentIndex = parseInt(panelSegmentIndex);

		} else {
			if (this.props.targetParams
				&& this.props.targetParams.gid
				&& (typeof this.props.targetParams.panelSegmentIndex === 'number')
			) {
				group_id = this.props.targetParams.gid;
				panelSegmentIndex = this.props.targetParams.panelSegmentIndex;
			}
			else if (this.props.fetchedGroup
				&& this.props.fetchedGroup._id
				&& (typeof this.props.fetchedGroupPanelSegment === 'number')
			) {
				group_id = this.props.fetchedGroup._id;
				panelSegmentIndex = this.props.fetchedGroupPanelSegment;
			}
		}

		return {
			group_id,
			panelSegmentIndex
		};
	}

	onChangeVisibility() {

		const {
			isSurveyActive,
			requiredOrientation,
			tuneoutQuestion
		} = this.state;

		let message = { event: PCORE_EVENT.METHOD_CALL, method: 'togglePlay', value: PCORE_EVENT.PAUSE };

		const playVideo = !(this.isShowingTuneoutModal || this.isShowingTuneoutQuestion || isSurveyActive || tuneoutQuestion);

		if (playVideo && document.visibilityState === 'visible' && requiredOrientation === this.getCurrentOrientation()) {
			message.value = PCORE_EVENT.PLAY;
		}

		this.sendMessage(message);
	}

	listenToVisibilityChange() {
		if (DeviceHelper.isMobile) {
			document.addEventListener('visibilitychange', this.onChangeVisibility);
		}
	}
	
	removeVisibilityChangeListener() {
		if (DeviceHelper.isMobile) {
			document.removeEventListener('visibilitychange', this.onChangeVisibility);	
		}
	}

	sendTuneOutMessage() {
		Logger.log(this.constructor.name, "sendTuneOutMessage()", 'SENDING TUNEOUT MESSAGE');
		const tuneoutMessage = { event: PCORE_EVENT.DISPLAY_TUNEOUT_QUESTIONS };
		this.sendMessage(tuneoutMessage);
	}

	showTuneoutQuestion() {
		const tuneoutQuestions = this.props.fetchedPanel.tuneout_questions;

		if (tuneoutQuestions && tuneoutQuestions.length > 0) {
			let tuneoutQuestion = tuneoutQuestions[0];
			const language = this.getLanguage();
			tuneoutQuestion = QUESTION_HELPERS.getLocalizedQuestion(tuneoutQuestion, language);
			tuneoutQuestion.hint_time = this.timecode;
			const localizedTitles = tuneoutQuestion.localized_title;

			if (localizedTitles) {
				const localizedTitle = localizedTitles[language];
				tuneoutQuestion.title = localizedTitle && localizedTitle.trim().length ? localizedTitle : tuneoutQuestion.title;
			}

			//Lock orientation to portrait when the tuneout question shows
			if (DeviceHelper.isMobile) this.lockOrientation(PORTRAIT_ORIENTATION);

			this.setState({ tuneoutQuestion });
		}
	}

	getLanguage() {
		const { intl, targetParams } = this.props;
		if (targetParams.lang) {
			return targetParams.lang;
		}
		if (intl.locale) {
			return intl.locale;
		}
		return 'en';
	}

	//:NOTE:: the language the STUDY is fielded in, which is not the same question as
	//getLanguage() answers. That one falls through to intl.locale, which is the respondent's
	//browser language whenever the link carries no ?lang= — right for choosing interface
	//strings, wrong for the agent.
	//
	//The agent is being told which language to write a follow-up in, and the questions are
	//only ever authored in the study's own language. A Greek-speaking respondent taking an
	//English study reports 'el', which nothing is authored in; the agent rejects a code
	//outside its list rather than falling back, so that respondent would get no follow-up at
	//all where today they get an English one.
	//
	//A study that declares no language is English, because that is what its questions are.
	getStudyLanguage() {
		const { fetchedGroup, targetParams } = this.props;
		const params = this.getParams();
		const panelSegment = fetchedGroup && fetchedGroup.panel_segment
			&& fetchedGroup.panel_segment[params && params.panelSegmentIndex];

		if (panelSegment && panelSegment.language_code) {
			return panelSegment.language_code;
		}
		if (targetParams && targetParams.lang) {
			return targetParams.lang;
		}
		return 'en';
	}

	onCloseModal() {
		if (this.isShowingTuneoutModal) {
			this.isShowingTuneoutModal = false;

			this.sendToggleLockMessage(false, false, false);

			if (DeviceHelper.isMobile && !this.isOrientationLocked && this.state.requiredOrientation === LANDSCAPE_ORIENTATION) {
				this.lockOrientation(LANDSCAPE_ORIENTATION);
			}
		}
	}

	handleTrackerUpdate(updatedAnswerTracker, updatedPathTracker) {
		// Merge rather than replace, and read from prevState — handleProgressUpdate()
		// writes the same two keys in this same React batch, so whichever setState lands
		// second must add to the first instead of overwriting it.
		this.setState((prevState) => ({
			pathTracker: prevState.pathTracker.concat(
				(updatedPathTracker || []).filter((path) => !prevState.pathTracker.includes(path))
			),
			answerTracker: prevState.answerTracker.concat(
				(updatedAnswerTracker || []).filter((aid) => !prevState.answerTracker.includes(aid))
			)
		}));
	}

	returnFeedbackType(screenCode) {
		if (screenCode) {
			if (screenCode !== 'thankyou') {
				return 'terminate';
			}
			return 'thankyou';
		}
		return '';
	}

	disableKeepAwake() {
		if (this.wakeLockSentinel) {
			document.removeEventListener('visibilitychange', this.requestWakeLockAgain);
			this.wakeLockSentinel.removeEventListener('release', this.onWakeLockRelease);
			this.wakeLockSentinel.release();
			this.wakeLockSentinel = null;
		}
	};

	async keepScreenAwake() {
		//Screen Wake Lock API
		//https://developer.mozilla.org/en-US/docs/Web/API/Screen_Wake_Lock_API
		const wakeLock = navigator.wakeLock;
		if (wakeLock) {
			await this.requestWakeLock();

			//Wake locks are commonly released automatically by the operational system when the document isn't visble anymore.
			//Example: switch from this tab to another, browser is minimized, device screen locks.
			//So we request the wake lock again if it was released and if the document becomes visible again.
			document.addEventListener('visibilitychange', this.requestWakeLockAgain);
		}
	};

	onWakeLockRelease() {
		if (document.visibilityState === 'visible') {
			this.requestWakeLock();
		}
	}

	async requestWakeLock() {
		try {
			this.wakeLockSentinel = await navigator.wakeLock.request('screen');
			if (this.wakeLockSentinel) {
				//If the wake lock was released when the document is still visible,
				//this means that the wake lock was released by the operational system by some special case.
				//Example: low battery, power saving mode
				//So in this cases we try to request the wake lock again
				this.wakeLockSentinel.addEventListener('release', this.onWakeLockRelease);
			}
		}
		catch (error) {
		}
	}

	async requestWakeLockAgain() {
		if (this.wakeLockSentinel && this.wakeLockSentinel.released && document.visibilityState === 'visible') {
			await this.requestWakeLock();
		}
	}

	//Update the timecode to 1 in user_content_profile when the user answers all the questions of the exit survey.
	//Portal Player also does this when Portal is accessed on desktop.
	//https://github.com/groupflix/portal-player/blob/master/players/js/player.core.js#L2637
	async completeUserContentProfile() {
		const { currentUser, fetchedEpisode, fetchedGroup } = this.props;

		if (!helpers.isGroupSurveyOnly(fetchedGroup)) {
			
			const episode_id = fetchedEpisode ? fetchedEpisode._id : null;
			const userId = currentUser.user_id;

			const params = this.getParams();
			const { group_id, panelSegmentIndex } = params;

			const payload = {
				browser: DeviceHelper.browser,
				title: fetchedGroup.title,
				panel_segment: panelSegmentIndex,
				group_id,
				device_id: '',
				device: DeviceHelper.device,
				episode_id,
				state: 'update',
				time: 1,
				user_id: userId
			};

			await this.updateUser(payload);
		}
	}

	processEmotionQuestionMap() {
		const emotionQuestionMap = this.props.fetchedPanel.emotion_question_map;
		if (emotionQuestionMap && emotionQuestionMap.like) {
			const language = this.getLanguage();
			this.emotionLikeAnswers = emotionQuestionMap.like.map((option) => {
				let answer = {
					emoji: getEmojiIcon(option.emoji_code)
				};

				const localizedPhrase = option.localized_phrase;
				if (localizedPhrase && localizedPhrase[language]) {
					answer.label = localizedPhrase[language];
				}

				return answer;
			});
		}
	}

	// Returns true when any question set at or after the given index has at least
	// one question that the survey module will actually render (a displayable
	// question — not a survey prompt or hidden conditional/logic question, both of
	// which the survey module skips). Used to detect when a survey-only study has
	// no remaining questions to show.
	hasDisplayableQuestionsFromSet(panel, fromQsetIndex) {
		if (!panel || !Array.isArray(panel.questions)) return false;

		return panel.questions.some((question) =>
			question.qset_index >= fromQsetIndex
			&& PANEL_HELPERS.isDisplayableQuestion(question)
		);
	}

	renderRotateDevice() {

		const currentOrientation = this.getCurrentOrientation();

		const {
			requiredOrientation,
			tuneoutQuestion
		} = this.state;

		if (currentOrientation && requiredOrientation && currentOrientation !== requiredOrientation) {

			let translationKey = localeMessages.rotate_device_portrait;

			if (requiredOrientation === LANDSCAPE_ORIENTATION) {
				translationKey = localeMessages.rotate_device_landscape;
			}
			else if (tuneoutQuestion) {
				translationKey = localeMessages.rotate_device_portrait_tuneout;
			}

			const message = this.props.intl.formatMessage(translationKey);

			return (
				<div id="rotateDevice">
					<div id="rotateDeviceText">
						{
							message
						}
					</div>
				</div>
			);
		}

		return null;
	}

	render() {
		Logger.log("PILOTLY PLAYER: render()", this.props, this.state);

		const {
			answerTracker,
			feedbackType,
			isResumingStudy,
			isSurveyActive,
			pathTracker,
			questionIndex,
			questionSetIndex,
			tuneoutQuestion,
			showLoader,
			videoError,
			receivedDisplaySurveyEvent
		} = this.state;

		const {
			fetchedGroup,
			fetchedPanel,
			intl,
			targetParams
		} = this.props;

		const language = this.getLanguage();

		Logger.log(this.constructor.name, "render()", 'RETURNIN FEEDBACK TYPE', feedbackType);

		if (feedbackType) {
			document.body.classList.remove('blackBackground');
			this.disableKeepAwake();
			this.stopUserActiveTimer();
			this.removeVisibilityChangeListener();
			return <Feedback feedbackType={feedbackType} {...this.props} />;
		}

		if (videoError) {
			this.disableKeepAwake();
			this.stopUserActiveTimer();
			this.removeVisibilityChangeListener();
			document.body.classList.remove('blackBackground');
			return <InvalidUrl />;
		}

		const showSurvey = isSurveyActive || tuneoutQuestion;

		return (
			<div className="pilotly-player">

				{
					this.renderRotateDevice()
				}

				{fetchedPanel && showSurvey ?
					<FadeIn show={showSurvey} addedClass={showSurvey ? "position-relative" : "position-absolute top-0"}>
						{
							// The open ended question component will just be rendered on mobile
							tuneoutQuestion ?
								<div className='question-container tuneout'>
									<OpenEnded
										fetchedGroup={fetchedGroup}
										handlePanelProgress={this.handleTuneoutAnswer}
										language={language}
										question={tuneoutQuestion}
										timeout={500}/>
								</div>
								:
								(
									receivedDisplaySurveyEvent ?
										<Survey
											{...this.props}
											timeout={500}
											language={language}
											settings={settings}
											intiQuestionIndex={questionIndex}
											intiQuestionSetIndex={questionSetIndex}
											isMobile={DeviceHelper.isMobile}
											isResumingStudy={isResumingStudy}
											answerTracker={answerTracker}
											pathTracker={pathTracker}
											questionContainerClass="pilotly-player"
											emotionLikeAnswers={this.emotionLikeAnswers}
											handleProgressUpdate={this.handleProgressUpdate}
											handlePanelResponse={this.handlePanelResponse}
											handleBadRespondents={this.handleBadRespondents}
											handleFollowUpRequest={this.handleSurveyFollowUpRequest}
											handleTermination={this.handleSurveyModuleTermination}
											handleQuestionIndexOverride={this.handleQuestionIndexOverride}
											handleTrackerUpdate={this.handleTrackerUpdate}/>
										: ''
								)
							
						}
					</FadeIn>
					: ''
				}

				<IframeComm
					attributes={this.attributes}
					postMessageData={this.state.postMessageData}
					handleReady={this.onReady}
					handleReceiveMessage={this.onReceiveMessage}/>

				{showLoader ? <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1000 }}><PageLoader loading={true} small={true} /></div> : null}

				<PilotlyModal
					className={DeviceHelper.isMobile ? 'mobile' : ''}
					header={this.state.modalTitle}
					id='pilotly-player-modal'
					message={this.state.modalMessage}
					actions={this.getModalBtns(this.state.modalType)}/>
				{/* <LandingFooter /> */}
			</div>
		);
	}
}

const mapStateToProps = state => ({
	tracker: state.GlobalAnalyticsAdapterReducer.gaTracker,
	globalAnalyticsAdapter: state.GlobalAnalyticsAdapterReducer.globalAnalyticsAdapter,
});

export default connect(mapStateToProps, actions)(injectIntl(PilotlyPlayer));



// WEBPACK FOOTER //
// ./src/components/global/PilotlyPlayer.js